/**
 * Module dependencies.
 */

import { Steps } from 'src/components/landingpage/components/steps/steps';
import { useTranslation } from 'next-i18next';
import StepperBackgroundSvg from 'src/assets/landingpage/hero-background-white.svg';
import styled from 'styled-components';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * `Background` styled component.
 */

const Background = styled.div`
  align-items: center;
  bottom: 1px;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100vw;
  z-index: 10;
`;

/**
 * `BackgroundContainer` styled component.
 */

const BackgroundContainer = styled.div`
  bottom: 0;
  display: block;
  height: auto;
  min-width: 100vw;
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

/**
 * `Container` styled component.
 */

const Container = styled.div`
  /* stylelint-disable no-descending-specificity */
  flex-shrink: 0;
  position: relative;
  width: 100%;
  z-index: 11;

  > div:first-child {
    margin-inline: auto;
    max-width: 1440px;
    width: 100%;

    > div {
      box-sizing: border-box;
      display: grid;
      gap: 32px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-template-rows: repeat(1, minmax(0, 1fr));
      padding: 48px 0 168px;
      position: relative;
      width: 100%;

      @media (min-width: 1024px) {
        margin-inline: auto;
        max-width: 1056px;
      }

      @media (max-width: 1119px) {
        padding-left: 32px;
        padding-right: 32px;
      }

      @media (max-width: 1023px) {
        display: flex;
        flex-direction: column;
        padding-top: 0;
      }

      @media (max-width: 767px) {
        padding-bottom: 70px;
      }

      @media (max-width: 419px) {
        padding-bottom: 70px;
      }
    }
  }

  /* Title section */
  .title {
    align-items: flex-start;
    box-sizing: border-box;
    content: stretch;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 10px;
    grid-area: 1 / 1;
    padding: 10px 0;
    position: relative;

    @media (max-width: 1023px) {
      grid-area: auto;
    }

    > div {
      color: #0a0a28;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 42px;
      font-weight: 900;
      line-height: 44px;
      position: relative;
      width: 100%;

      @media (max-width: 419px) {
        font-size: 32px;
      }

      p {
        margin-bottom: 0;

        &:last-child {
          margin-bottom: initial;
        }
      }
    }
  }

  /* Stepper container */
  .stepper {
    align-items: flex-start;
    align-self: flex-start;
    content: stretch;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 32px;
    grid-area: 1 / 2 / auto / span 2;
    position: relative;

    @media (max-width: 1023px) {
      grid-area: auto;
    }
  }

  /* Stepper progress bar */
  .progress {
    align-items: flex-start;
    box-sizing: border-box;
    content: stretch;
    display: flex;
    flex-shrink: 0;
    gap: 259px;
    height: 64px;
    padding: 0 0 64px;
    position: relative;
    width: 100%;
  }

  /* Icons container */
  .icons {
    align-items: flex-start;
    content: stretch;
    display: flex;
    flex-basis: 0;
    flex-grow: 1;
    flex-shrink: 0;
    justify-content: space-between;
    min-height: 1px;
    min-width: 1px;
    position: relative;

    &::before {
      background-color: #00cddc;
      bottom: 0;
      content: '';
      left: 32px;
      position: absolute;
      right: 32px;
      top: 0;
    }

    /* Progress line */
    > div:first-child {
      height: 0;
      left: 32px;
      position: absolute;
      right: 32.33px;
      top: calc(50% + 0.18px);
    }
  }

  /* Icon wrapper */
  .icon {
    flex-shrink: 0;
    height: 63.64px;
    position: relative;
    width: 63.64px;

    > div:first-child {
      inset: 0;
      position: absolute;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  /* Verification icon inner */
  .icon.verification > div:last-child {
    inset: 16.67%;
    overflow: clip;
    position: absolute;

    > div {
      bottom: -0.01%;
      left: 0;
      position: absolute;
      right: -0.01%;
      top: 0;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  /* Location icon inner */
  .icon.location > div:last-child {
    aspect-ratio: 14 / 23;
    left: 29.17%;
    overflow: clip;
    position: absolute;
    right: 30.75%;
    top: 10.36px;

    > div {
      display: contents;
      inset: 0;
      position: absolute;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  /* Report icon inner */
  .icon.report > div:last-child {
    aspect-ratio: 21 / 29;
    left: 27.08%;
    overflow: clip;
    position: absolute;
    right: 29.17%;
    top: 13.31px;

    > div {
      bottom: 0;
      display: contents;
      left: 0;
      position: absolute;
      right: 0.01%;
      top: 0;

      > div {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0.01%;
        top: 0;

        svg {
          display: block;
          height: 100%;
          width: 100%;
        }
      }
    }
  }

  /* Steps content */
  .steps {
    align-items: flex-start;
    content: stretch;
    display: flex;
    flex-shrink: 0;
    gap: 44px;
    position: relative;
    width: 100%;
  }

  /* Step wrapper */
  .step {
    align-items: flex-start;
    border-radius: 10px;
    color: #0a0a28;
    content: stretch;
    display: flex;
    flex-basis: 0;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 0;
    gap: 8px;
    min-height: 1px;
    min-width: 1px;
    position: relative;

    > p {
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      position: relative;
      width: 100%;
    }

    .title-text {
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 24px;
      font-weight: 900;
      line-height: 32px;
      position: relative;
      width: 100%;

      p {
        margin-bottom: 0;

        &:last-child {
          margin-bottom: initial;
        }
      }
    }
  }

  /* Step 2 wrapper */
  .step-wrapper {
    align-items: center;
    display: flex;
    flex-basis: 0;
    flex-grow: 1;
    flex-shrink: 0;
    justify-content: center;
    min-height: 1px;
    min-width: 1px;
    position: relative;

    > div {
      flex: none;
      width: 100%;
    }
  }

  /* Step 2 specific */
  .step-2 {
    align-items: flex-start;
    border-radius: 24px;
    content: stretch;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    width: 100%;

    > p {
      color: #0a0a28;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      margin-bottom: 0;
      position: relative;
      white-space: pre-wrap;
      width: 100%;
    }

    .title-text {
      color: #0a0a28;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 24px;
      font-weight: 900;
      line-height: 32px;
      position: relative;
      width: 100%;

      p {
        margin-bottom: 0;

        &:last-child {
          margin-bottom: initial;
        }
      }
    }
  }

  /* stylelint-enable no-descending-specificity */
`;

/**
 * Export `StepperSection` component.
 */

export function StepperSection() {
  const { t } = useTranslation('landingpage');

  return (
    <Wrapper>
      <Background data-name={'background'} id={'stepper-background'}>
        <BackgroundContainer>
          <StepperBackgroundSvg />
        </BackgroundContainer>
      </Background>

      <Container
        data-name={'section-stepper'}
        id={'section-stepper'}
        style={{
          opacity: 0,
          transform: 'translateY(250px)',
          transition: 'opacity 0.1s ease-out, transform 0.1s ease-out'
        }}
      >
        <div>
          <div>
            <div className={'title'} data-name={'h1'}>
              <div>
                <p>{t('stepper.title.line1')}</p>
                <p>{t('stepper.title.line2')}</p>
                <p>{t('stepper.title.line3')}</p>
              </div>
            </div>

            <div className={'stepper'} data-name={'Stepper'}>
              <Steps />
            </div>
          </div>
        </div>
      </Container>
    </Wrapper>
  );
}
